import { z } from "zod";

/**
 * THE VERSION FLOOR — the endpoint that makes "the app is a version you cannot recall"
 * survivable.
 *
 * The app asks at launch. Below the floor it shows a blocking screen with a link to the
 * store instead of failing in whatever way an outdated client fails in — usually a parse
 * error on a screen that has nothing to do with the change.
 *
 * Raising the floor is a deliberate act with a cost: everyone below it is stopped until
 * they update. It is for a change that genuinely cannot be made backwards compatible, not
 * for tidiness.
 */
export const minimumVersionSchema = z.object({
  /** Semver. Builds below this are blocked. */
  minimum: z.string(),
  /** Semver. Builds below this get a dismissible nudge. */
  recommended: z.string(),
  /** Where the update lives, per platform. */
  storeUrl: z.object({ ios: z.string(), android: z.string(), web: z.string() }),
});

export type MinimumVersion = z.infer<typeof minimumVersionSchema>;

export const healthSchema = z.object({
  status: z.literal("ok"),
  env: z.enum(["dev", "staging", "production"]),
  version: z.string(),
  /** Migration count applied to the database this service is talking to. */
  schemaVersion: z.number(),
});

export type Health = z.infer<typeof healthSchema>;
